import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  CredentialDocStatus,
  CredentialDocumentRow,
  credentialReviewService,
} from '../services/credentialReviewService';

// FEAT-046 — Admin Credential Review Queue page (MVP-18.20, MVP-18.21).
//
// Lists uploaded credential documents with filters (status + caregiver id).
// Clicking a row opens a side preview with metadata + storageUri pointer
// (admin-only). Accepting writes the badge entitlement implicitly via the
// PATCH endpoint (FEAT-039 derives the badge from the document state).
// Rejecting requires a short reason that flows back to the caregiver
// surface as the "Note from our team" copy.

const STATUS_OPTIONS: Array<CredentialDocStatus | 'ALL'> = [
  'ALL',
  'PENDING_REVIEW',
  'ACCEPTED',
  'REJECTED',
];

const STATUS_COLOR: Record<CredentialDocStatus, 'warning' | 'success' | 'default'> = {
  PENDING_REVIEW: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'default',
};

function caregiverDisplay(doc: CredentialDocumentRow): string {
  const cg = doc.caregiver;
  if (!cg) return doc.caregiverId;
  const name = `${cg.firstName ?? ''} ${cg.lastName ?? ''}`.trim();
  return name || cg.email;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface RejectDialogState {
  open: boolean;
  doc: CredentialDocumentRow | null;
  reason: string;
  submitting: boolean;
  error: string | null;
}

const CredentialReviewQueuePage: React.FC = () => {
  const [rows, setRows] = useState<CredentialDocumentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [status, setStatus] = useState<CredentialDocStatus | 'ALL'>('PENDING_REVIEW');
  const [caregiverFilter, setCaregiverFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [reject, setReject] = useState<RejectDialogState>({
    open: false,
    doc: null,
    reason: '',
    submitting: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await credentialReviewService.listQueue({
        status: status === 'ALL' ? undefined : status,
        caregiverId: caregiverFilter.trim() || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(result.documents);
      setTotal(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [status, caregiverFilter, page, rowsPerPage]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const selectedDoc = rows.find((r) => r.id === selectedId) ?? null;

  const handleAccept = async (doc: CredentialDocumentRow) => {
    setActionInFlight(true);
    setActionError(null);
    try {
      const result = await credentialReviewService.applyDecision({
        documentId: doc.id,
        decision: 'ACCEPTED',
      });
      setRows((prev) =>
        prev.map((r) => (r.id === doc.id ? result.document : r)),
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to accept');
    } finally {
      setActionInFlight(false);
    }
  };

  const openReject = (doc: CredentialDocumentRow) => {
    setReject({ open: true, doc, reason: '', submitting: false, error: null });
  };

  const submitReject = async () => {
    if (!reject.doc) return;
    if (!reject.reason.trim()) {
      setReject((prev) => ({
        ...prev,
        error: 'Please share a short note so the caregiver knows what to fix.',
      }));
      return;
    }
    setReject((prev) => ({ ...prev, submitting: true, error: null }));
    try {
      const result = await credentialReviewService.applyDecision({
        documentId: reject.doc.id,
        decision: 'REJECTED',
        reason: reject.reason.trim(),
      });
      setRows((prev) =>
        prev.map((r) => (r.id === reject.doc!.id ? result.document : r)),
      );
      setReject({ open: false, doc: null, reason: '', submitting: false, error: null });
    } catch (err) {
      setReject((prev) => ({
        ...prev,
        submitting: false,
        error: err instanceof Error ? err.message : 'Failed to record decision',
      }));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" data-testid="credential-review-queue-title">
            Credential Review Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review caregiver-uploaded credentials. Accepted documents become
            badge entitlements on the public profile.
          </Typography>
        </Box>
        <IconButton
          onClick={() => fetchData()}
          aria-label="Refresh queue"
          data-testid="credential-review-refresh"
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="credential-review-status-label">Status</InputLabel>
          <Select
            labelId="credential-review-status-label"
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(0);
            }}
            inputProps={{ 'data-testid': 'credential-review-status-filter' }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Caregiver id"
          value={caregiverFilter}
          onChange={(e) => {
            setCaregiverFilter(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 280 }}
          inputProps={{ 'data-testid': 'credential-review-caregiver-filter' }}
        />
      </Stack>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-testid="credential-review-queue-error"
        >
          {error}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="credential-review-action-error"
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper sx={{ flex: 2 }}>
          <TableContainer>
            <Table size="small" data-testid="credential-review-queue-table">
              <TableHead>
                <TableRow>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Caregiver</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 4 }}
                      >
                        No documents match these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((doc) => (
                    <TableRow
                      key={doc.id}
                      hover
                      onClick={() => setSelectedId(doc.id)}
                      sx={{ cursor: 'pointer' }}
                      selected={selectedId === doc.id}
                      data-testid={`credential-review-row-${doc.id}`}
                    >
                      <TableCell>
                        {new Date(doc.uploadedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{caregiverDisplay(doc)}</TableCell>
                      <TableCell>{doc.title}</TableCell>
                      <TableCell>{doc.docType.replace(/_/g, ' ')}</TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status.replace(/_/g, ' ')}
                          size="small"
                          color={STATUS_COLOR[doc.status]}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            color="success"
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            disabled={
                              actionInFlight || doc.status === 'ACCEPTED'
                            }
                            onClick={() => handleAccept(doc)}
                            data-testid={`credential-review-accept-${doc.id}`}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            disabled={
                              actionInFlight || doc.status === 'REJECTED'
                            }
                            onClick={() => openReject(doc)}
                            data-testid={`credential-review-reject-${doc.id}`}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Paper>

        <Paper sx={{ flex: 1, p: 2, minHeight: 320 }} data-testid="credential-review-preview">
          {selectedDoc ? (
            <Stack spacing={1}>
              <Typography variant="h6">{selectedDoc.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {caregiverDisplay(selectedDoc)} •{' '}
                {selectedDoc.docType.replace(/_/g, ' ')}
              </Typography>
              <Chip
                label={selectedDoc.status.replace(/_/g, ' ')}
                size="small"
                color={STATUS_COLOR[selectedDoc.status]}
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography variant="body2">
                <strong>Mime:</strong> {selectedDoc.mimeType}
              </Typography>
              <Typography variant="body2">
                <strong>Size:</strong> {formatSize(selectedDoc.sizeBytes)}
              </Typography>
              <Typography variant="body2">
                <strong>Uploaded:</strong>{' '}
                {new Date(selectedDoc.uploadedAt).toLocaleString()}
              </Typography>
              {selectedDoc.expiresAt ? (
                <Typography variant="body2">
                  <strong>Expires:</strong>{' '}
                  {new Date(selectedDoc.expiresAt).toLocaleDateString()}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>Storage URI:</strong> {selectedDoc.storageUri}
              </Typography>
              {selectedDoc.notes ? (
                <Typography variant="body2">
                  <strong>Caregiver notes:</strong> {selectedDoc.notes}
                </Typography>
              ) : null}
              {selectedDoc.reviewDecision?.reason ? (
                <Typography variant="body2">
                  <strong>Previous decision note:</strong>{' '}
                  {selectedDoc.reviewDecision.reason}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a document on the left to preview metadata before
              deciding.
            </Typography>
          )}
        </Paper>
      </Box>

      <Dialog
        open={reject.open}
        onClose={() =>
          setReject({
            open: false,
            doc: null,
            reason: '',
            submitting: false,
            error: null,
          })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send a short note back to the caregiver</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Briefly explain what would help — the caregiver will see this on
            their profile so they know how to share an updated copy.
          </DialogContentText>
          <TextField
            autoFocus
            label="Reason"
            value={reject.reason}
            onChange={(e) =>
              setReject((prev) => ({ ...prev, reason: e.target.value }))
            }
            fullWidth
            multiline
            minRows={3}
            inputProps={{
              maxLength: 1000,
              'data-testid': 'credential-review-reject-reason',
            }}
          />
          {reject.error ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {reject.error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReject({
                open: false,
                doc: null,
                reason: '',
                submitting: false,
                error: null,
              })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={submitReject}
            disabled={reject.submitting}
            data-testid="credential-review-reject-submit"
          >
            {reject.submitting ? 'Saving…' : 'Send note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CredentialReviewQueuePage;
